<script setup lang="ts">
import {
  useSettingStore,
} from 'pinia-store'
import { Popover } from 'flowbite'
import { useRoute, useRouter } from 'vue-router'
import type { InstanceOptions, PopoverInterface, PopoverOptions } from 'flowbite'
import { stripHtml } from '@general-function'
import { useI18n } from '#imports'

const useSetting = useSettingStore()
const { setLocale } = useI18n()

// set the popover content element
const route = useRoute()
const router = useRouter()
const loading = ref(false)
const el = document && document.getElementById('popover-language')
const elBtn = document && document.getElementById('button-language')
const languages = computed(() => useSetting.languages)
let popover: PopoverInterface
// options with default values
const options: PopoverOptions = {
  placement: 'left',
  triggerType: 'click',
  offset: 10,
  onHide: () => {
  },
  onShow: () => {
    //
  },
  onToggle: () => {
    //
  },
}
function init() {
  if (!document || typeof document !== 'object')
    return

  const targetEl: HTMLElement | null = document.getElementById('popover-language')

  // set the element that trigger the popover using hover or click
  const triggerEl: HTMLElement | null = document.getElementById('button-language')

  // instance options object
  const instanceOptions: InstanceOptions = {
    id: 'popover-language',
    override: true,
  }

  /*
     *targetEl: required
     *triggerEl: required
     * options: optional
     * instanceOptions: optional
     */
  popover = new Popover(
    targetEl,
    triggerEl,
    options,
    instanceOptions,
  )
}
init()
function handleClick() {
  if (el && el.classList.contains('popover-language')) {
    el.classList.remove('max-lg:!hidden')
    el.classList.remove('max-lg:!flex')
  }
  if (elBtn && elBtn.classList.contains('max-lg:z-101')) {
    elBtn.classList.remove('max-lg:z-101')
    elBtn.classList.add('max-lg:z-10')
  }
}
function handleClose() {
  if (el && el.classList.contains('visible'))
    el.classList.add('max-lg:!hidden')

  if (elBtn && elBtn.classList.contains('max-lg:z-10')) {
    elBtn.classList.remove('max-lg:z-10')
    elBtn.classList.add('max-lg:z-101')
  }
}
function handleLang(val) {
  if (route?.params?.language && route?.params?.language.toLowerCase() !== val.label_uri.toLowerCase() && !loading.value) {
    loading.value = true
    useSetting.setLanguage(val)
    loading.value = false
    setLocale(val.label_uri.toLowerCase())
    return router.push({
      params: {
        ...route?.params,
        language: val.label_uri,
      },
      query: {
        ...route?.query,
      },
      force: true,
    }).then(() => { router.go(0) })
  }
}
onBeforeUnmount(() => {
  loading.value = false
})
</script>

<template >
  <button
    v-if="languages && languages.length > 1"
    id="button-language"
    type="button"
    class="pba-btn-lang fixed right-[30px] h-[46px] w-[46px] flex cursor-pointer items-center justify-center rounded-full bg-[#ffff] lg:bottom-[100px] max-lg:bottom-[140px] lg:z-[20] max-lg:z-100"
    @click="handleClick"
  >
    <svg class="h-6 w-6 text-[var(--color-secondary)]" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
      <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m13 19 3.5-9 3.5 9m-6.125-2h5.25M3 7h7m0 0h2m-2 0c0 1.63-.793 3.926-2.239 5.655M7.5 6.818V5m.261 7.655C6.79 13.82 5.521 14.725 4 15m3.761-2.345L5 10m2.761 2.655L10.2 15"/>
    </svg>
  </button>
  <div
    v-if="languages && languages.length > 1"
    id="popover-language"
    data-popover
    role="tooltip"
    class="popover-language invisible z-100 border-none bg-white opacity-0 transition-opacity duration-300 max-lg:flex-col"
  >
    <div
      v-if="popover"
      class="w-full items-start items-center justify-end lg:hidden max-lg:flex max-lg:p-6"
    >
      <button
        type="button"
        title="close"
        class="flex items-center text-gray"
        @click="handleClose"
      >
        <IClose size="24"/>
        <span class="sr-only">Close modal</span>
      </button>
    </div>
    <div class="popup-inner max-lg:w-full max-lg:px-6 max-lg:pb-6 max-lg:pt-14 max-lg:text-center">
      <div class="rounded-t-lg px-3 py-2">
        <h3 class="text-black font-bold max-lg:text-[21px]">
          Language
        </h3>
      </div>
      <div class="flex flex-col overflow-y-auto px-4 lg:max-h-[160px] lg:items-start lg:justify-start lg:rounded-md lg:text-left lg:shadow-sm">
        <button
          v-for="(el) in languages"
          :id="`button-lang-${el.label_uri}`"
          :key="el.label_uri"
          type="button"
          :disabled="(route?.params?.language && route?.params?.language.toLowerCase() === el.label_uri.toLowerCase() ? true : false) || loading"
          :class="`${route?.params?.language && route?.params?.language.toLowerCase() === el.label_uri.toLowerCase() ? 'text-[var(--color-secondary)] font-medium' : 'text-gray-600'} lg:py-2 max-lg:py-3 font-secondary hover:!text-[var(--color-secondary)] lg:text-sm max-lg:text-[18px] focus:z-10`"
          @click="handleLang(el)"
          v-html="stripHtml(el.label_name as string, false)"
        >
        </button>
      </div>

      <div data-popper-arrow></div>
    </div>
  </div>
</template>

<style lang="scss">
@import './button-language/component.scss';
</style>
